<script>
  export let title = "";
  export let content = "";
  export let galleryImages = [];
  export let directionsUrls = null;
  export let linkedScenes = [];
  export let onClose = () => {};
  export let onOpen360 = () => {};
  export let hasNextLocation = false;
  export let onNextLocation = () => {};
  export let showRestart = false;
  export let onRestart = () => {};
  export let nextStopNumber = null;
  export let nextStopPoi = null;
  export let nextStopBadgeBg = "var(--theme-color, #000)";
  export let nextStopBadgeText = "#fff";
  export let ariaLabel = `Popup: ${title}`;
  export let obscured = false;

  import UAParser from "ua-parser-js";
  import CloseButton from "./CloseButton.svelte";
  import Icon from "@iconify/svelte";
  import { fly, fade } from "svelte/transition";
  import { focusTrap } from "./utils.js";
  import { buildNumericMarkerHtml } from "./numericPoiMarker.js";
  import { onDestroy, onMount, tick } from "svelte";
  import { Splide, SplideSlide } from "@splidejs/svelte-splide";
  import "@splidejs/splide/dist/css/splide.min.css";

  let parser = new UAParser();
  let isMobile = parser.getResult().device["type"] == "mobile";

  let visible = true;
  let popupContentEl;
  let scrollContainer;
  let splideComponent;
  let galleryFrameEl;
  let resizeObserver;
  let directionsDropdownOpen = false;
  let initializedFocus = false;

  $: hasGallery = Array.isArray(galleryImages) && galleryImages.length > 0;
  $: isSingleImage = galleryImages.length <= 1;
  $: imagesKey = galleryImages.map((img) => img.src).join("|");
  $: hasDirections = Boolean(directionsUrls);
  $: hasScenes = linkedScenes.length > 0;
  $: showOverlayActions = hasDirections || hasScenes;
  $: showSequenceNav = hasNextLocation || showRestart;
  $: effectiveNextStopBg =
    nextStopPoi?.bgColor?.trim() ? nextStopPoi.bgColor.trim() : nextStopBadgeBg;
  $: nextStopBadgeHtml =
    nextStopNumber != null
      ? buildNumericMarkerHtml(nextStopPoi ?? {}, {
          bgColor: effectiveNextStopBg,
          textColor: nextStopBadgeText,
          strokeColor: nextStopBadgeText,
          number: nextStopNumber,
          size: 50,
        })
      : "";

  $: splideOptions = {
    type: "slide",
    pagination: false,
    rewind: !isSingleImage,
    perMove: 1,
    perPage: 1,
    arrows: false,
    drag: galleryImages.length > 1,
    width: "100%",
  };

  $: if (!visible) initializedFocus = false;
  $: if (visible && !obscured && !initializedFocus) {
    initializedFocus = true;
    focusPopup();
  }

  async function focusPopup() {
    await tick();
    if (popupContentEl) popupContentEl.scrollTop = 0;
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
      scrollContainer.focus({ preventScroll: true });
    }
  }

  function scheduleSplideRefresh() {
    const splide = splideComponent?.splide;
    if (!splide) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => splide.refresh());
    });
  }

  function goPrev() {
    splideComponent?.splide?.go("<");
  }

  function goNext() {
    splideComponent?.splide?.go(">");
  }

  onMount(async () => {
    await tick();
    const splide = splideComponent?.splide;
    if (splide) {
      splide.on("mounted", scheduleSplideRefresh);
      scheduleSplideRefresh();
    }
    if (galleryFrameEl && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => scheduleSplideRefresh());
      resizeObserver.observe(galleryFrameEl);
    }
  });

  $: if (imagesKey && splideComponent?.splide) {
    tick().then(scheduleSplideRefresh);
  }

  onDestroy(() => {
    resizeObserver?.disconnect();
    splideComponent?.destroy?.();
  });

  function closePopup() {
    visible = false;
    onClose();
  }

  function sceneTitleForDisplay(sceneTitle) {
    return (sceneTitle || "").replace(/\[br\]/g, " ");
  }

  function toggleDirectionsDropdown() {
    directionsDropdownOpen = !directionsDropdownOpen;
  }

  function handleOpenDirections(url) {
    if (url) window.open(url, "_blank");
    directionsDropdownOpen = false;
  }

  function handleOpen360() {
    onOpen360();
  }
</script>

{#if visible}
  <div
    class="popup-modal"
    class:has-image={hasGallery}
    class:has-seq-nav={showSequenceNav}
    class:obscured
    inert={obscured}
    aria-hidden={obscured ? "true" : undefined}
    use:focusTrap
  >
    <div class="popup-area">
      <div class="popup-center-column">
        <div class="popup-window-wrap">
          <div class="popup-window" transition:fly={{ y: 800, duration: 500 }}>
            <div class="popup-layout">
              <div class="popup-content-column">
              <div class="popup-content" bind:this={popupContentEl}>
                <div class="popup">
                {#if hasGallery}
                  <div class="image column">
                    <div class="gallery-carousel" class:has-nav={!isSingleImage}>
                      {#if !isSingleImage}
                        <button
                          type="button"
                          class="gallery-arrow gallery-arrow-prev"
                          on:click|stopPropagation={goPrev}
                          aria-label="Previous image"
                        >
                          <Icon icon="material-symbols:chevron-left-rounded" width="20" height="20" />
                        </button>
                      {/if}
                      <div class="gallery-frame" bind:this={galleryFrameEl}>
                        {#key imagesKey}
                          <Splide options={splideOptions} bind:this={splideComponent}>
                            {#each galleryImages as image (image.src)}
                              <SplideSlide>
                                <img
                                  src={image.src}
                                  alt={image.alt ?? ""}
                                  class="popup-image"
                                  on:load={scheduleSplideRefresh}
                                />
                              </SplideSlide>
                            {/each}
                          </Splide>
                        {/key}
                      </div>
                      {#if !isSingleImage}
                        <button
                          type="button"
                          class="gallery-arrow gallery-arrow-next"
                          on:click|stopPropagation={goNext}
                          aria-label="Next image"
                        >
                          <Icon icon="material-symbols:chevron-right-rounded" width="20" height="20" />
                        </button>
                      {/if}
                    </div>
                    {#if showOverlayActions}
                      <div class="items">
                        {#if hasDirections}
                          <div class="overlay-action">
                            <button
                              type="button"
                              class="item directions-item"
                              tabindex="0"
                              on:click|stopPropagation={toggleDirectionsDropdown}
                              aria-label="Get directions"
                              aria-expanded={directionsDropdownOpen}
                              aria-haspopup="menu"
                            >
                              <div class="icon">
                                <Icon icon="material-symbols:directions-outline" width="1em" height="1em" />
                              </div>
                              <span>Directions</span>
                            </button>
                            {#if directionsDropdownOpen}
                              <div class="overlay-dropdown" role="menu" in:fly={{ y: 6, duration: 120 }} out:fly={{ y: 6, duration: 96 }}>
                                <button type="button" class="overlay-dropdown-option" role="menuitem" on:click|stopPropagation={() => handleOpenDirections(directionsUrls.google)}>
                                  <Icon icon="simple-icons:googlemaps" width="1em" height="1em" />
                                  <span>Google Maps</span>
                                </button>
                                <button type="button" class="overlay-dropdown-option" role="menuitem" on:click|stopPropagation={() => handleOpenDirections(directionsUrls.apple)}>
                                  <Icon icon="simple-icons:apple" width="1em" height="1em" />
                                  <span>Apple Maps</span>
                                </button>
                                <button type="button" class="overlay-dropdown-option" role="menuitem" on:click|stopPropagation={() => handleOpenDirections(directionsUrls.waze)}>
                                  <Icon icon="simple-icons:waze" width="1em" height="1em" />
                                  <span>Waze</span>
                                </button>
                              </div>
                            {/if}
                          </div>
                        {/if}
                        {#if hasScenes}
                          <div class="overlay-action">
                            <button
                              type="button"
                              class="item scenes-item"
                              tabindex="0"
                              on:click|stopPropagation={handleOpen360}
                              aria-label={linkedScenes.length === 1 ? `Open 360: ${sceneTitleForDisplay(linkedScenes[0].title)}` : "Open 360s"}
                            >
                              <div class="icon">
                                <Icon icon="material-symbols:360" width="1em" height="1em" />
                              </div>
                              <span>{linkedScenes.length === 1 ? "Open 360" : "Open 360s"}</span>
                            </button>
                          </div>
                        {/if}
                      </div>
                    {/if}
                  </div>
                {/if}

                <div class="content column">
                  <div
                    class="content-inner"
                    role="dialog"
                    aria-label={ariaLabel}
                    tabindex="0"
                    bind:this={scrollContainer}
                  >
                    <h2 class="title">{@html title}</h2>
                    {@html content}
                  </div>
                  {#if !hasGallery && showOverlayActions}
                    <div class="content-actions">
                      {#if hasDirections}
                        <div class="overlay-action">
                          <button
                            type="button"
                            class="item directions-item"
                            tabindex="0"
                            on:click|stopPropagation={toggleDirectionsDropdown}
                            aria-label="Get directions"
                            aria-expanded={directionsDropdownOpen}
                            aria-haspopup="menu"
                          >
                            <div class="icon">
                              <Icon icon="material-symbols:directions-outline" width="1em" height="1em" />
                            </div>
                            <span>Directions</span>
                          </button>
                          {#if directionsDropdownOpen}
                            <div class="overlay-dropdown" role="menu" in:fly={{ y: 6, duration: 120 }} out:fly={{ y: 6, duration: 96 }}>
                              <button type="button" class="overlay-dropdown-option" role="menuitem" on:click|stopPropagation={() => handleOpenDirections(directionsUrls.google)}>
                                <Icon icon="simple-icons:googlemaps" width="1em" height="1em" />
                                <span>Google Maps</span>
                              </button>
                              <button type="button" class="overlay-dropdown-option" role="menuitem" on:click|stopPropagation={() => handleOpenDirections(directionsUrls.apple)}>
                                <Icon icon="simple-icons:apple" width="1em" height="1em" />
                                <span>Apple Maps</span>
                              </button>
                              <button type="button" class="overlay-dropdown-option" role="menuitem" on:click|stopPropagation={() => handleOpenDirections(directionsUrls.waze)}>
                                <Icon icon="simple-icons:waze" width="1em" height="1em" />
                                <span>Waze</span>
                              </button>
                            </div>
                          {/if}
                        </div>
                      {/if}
                      {#if hasScenes}
                        <div class="overlay-action">
                          <button
                            type="button"
                            class="item scenes-item"
                            tabindex="0"
                            on:click|stopPropagation={handleOpen360}
                            aria-label={linkedScenes.length === 1 ? `Open 360: ${sceneTitleForDisplay(linkedScenes[0].title)}` : "Open 360s"}
                          >
                            <div class="icon">
                              <Icon icon="material-symbols:360" width="1em" height="1em" />
                            </div>
                            <span>{linkedScenes.length === 1 ? "Open 360" : "Open 360s"}</span>
                          </button>
                        </div>
                      {/if}
                    </div>
                  {/if}
                </div>
              </div>
              </div>
              {#if showSequenceNav}
                <div class="popup-seq-nav" transition:fade={{ duration: 180 }}>
                  {#if hasNextLocation}
                    <button
                      type="button"
                      class="popup-seq-nav-btn popup-seq-nav-btn-next"
                      on:click|stopPropagation={onNextLocation}
                      aria-label={nextStopNumber != null ? `Go to stop ${nextStopNumber}` : "Next stop"}
                    >
                      <span>Next Stop</span>
                      {#if nextStopBadgeHtml}
                        <span class="popup-seq-nav-stop-badge" aria-hidden="true">{@html nextStopBadgeHtml}</span>
                      {/if}
                      <Icon icon="material-symbols:chevron-right-rounded" aria-hidden="true" />
                    </button>
                  {:else if showRestart}
                    <button
                      type="button"
                      class="popup-seq-nav-btn popup-seq-nav-btn-restart"
                      on:click|stopPropagation={onRestart}
                      aria-label="Start over from stop 1"
                    >
                      <Icon icon="material-symbols:restart-alt-rounded" aria-hidden="true" />
                      <span>Start Over</span>
                    </button>
                  {/if}
                </div>
              {/if}
              </div>
            <div class="close-button-container">
              <CloseButton
                close={closePopup}
                color="#ffffff"
                useAbsolutePosition={false}
                marginBottom={isMobile ? "0" : "1rem"}
              />
            </div>
          </div>
        </div>
      </div>
      </div>
      <div class="popup-bg" on:click={closePopup} transition:fade={{ duration: 500 }}></div>
    </div>
  </div>
{/if}

<style>
  .popup {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .column {
    display: flex;
    flex-direction: column;
    flex-basis: 100%;
    flex: 1;
    height: 100%;
  }

  .image {
    background: #000;
    background-repeat: no-repeat;
    background-size: cover;
    position: relative;
  }

  .gallery-carousel {
    position: relative;
    width: 100%;
    height: 100%;
    background: #000;
  }

  .gallery-carousel::before,
  .gallery-carousel::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: 18%;
    max-width: 96px;
    z-index: 2;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .gallery-carousel.has-nav::before,
  .gallery-carousel.has-nav::after {
    opacity: 1;
  }

  .gallery-carousel::before {
    left: 0;
    background: linear-gradient(to right, rgba(0, 0, 0, 0.42), transparent);
  }

  .gallery-carousel::after {
    right: 0;
    background: linear-gradient(to left, rgba(0, 0, 0, 0.42), transparent);
  }

  .gallery-frame {
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: #000;
  }

  .gallery-frame :global(.splide__slide) {
    background: #000;
  }

  .gallery-frame :global(.splide),
  .gallery-frame :global(.splide__track),
  .gallery-frame :global(.splide__list),
  .gallery-frame :global(.splide__slide) {
    height: 100%;
  }

  .gallery-frame :global(.splide) {
    width: 100%;
  }

  .gallery-arrow {
    position: absolute;
    top: 50%;
    z-index: 4;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.28);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    color: rgba(255, 255, 255, 0.82);
    opacity: 0.72;
    cursor: pointer;
    box-shadow: none;
    transition:
      opacity 0.2s ease,
      background 0.2s ease,
      color 0.2s ease;
  }

  .gallery-arrow:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.42);
    color: #fff;
  }

  .gallery-arrow:active {
    opacity: 0.88;
    background: rgba(0, 0, 0, 0.5);
  }

  .gallery-arrow:focus-visible {
    opacity: 1;
    outline: 1px solid rgba(255, 255, 255, 0.45);
    outline-offset: 2px;
  }

  .gallery-arrow-prev {
    left: 12px;
  }

  .gallery-arrow-next {
    right: 12px;
  }

  .title {
    margin: 2rem 0;
    margin-bottom: 0;
    font-family: var(--font-heading), sans-serif;
    font-size: 2rem;
    line-height: 1;
  }

  .content {
    justify-content: center;
    font-family: var(--font-body), sans-serif;
    line-height: 1.5;
  }

  .content-inner {
    padding: 2rem;
    padding-top: 0;
    overflow-y: scroll;
    max-height: 90vh;
  }

  .content-inner:focus {
    outline: none;
  }

  .content-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    padding: 0 2rem 2rem;
  }

  .content-actions .overlay-action {
    position: relative;
    flex: 0 1 auto;
  }

  .content-actions .item {
    background: #fff;
    color: #000;
    border: 2px solid transparent;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  }

  .content-actions .item:hover {
    background: #000;
    color: #fff;
    border-color: #000;
  }

  .items {
    position: absolute;
    width: 100%;
    bottom: 0;
    left: 0;
    right: 0;
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    padding: 5%;
    justify-content: center;
    gap: 8px;
    z-index: 4;
  }

  .overlay-action {
    position: relative;
    flex: 0 1 auto;
  }

  .item {
    appearance: none;
    border: none;
    text-align: inherit;
    color: inherit;
    cursor: pointer;
    background: rgba(var(--popup-bg-color-rgb), 0.85);
    border-radius: 5px;
    margin: 5px;
    padding: 0.5rem 2rem;
    font-size: 1.5rem;
    display: flex;
    flex-direction: row;
    flex: 0;
    font-family: var(--font-heading), sans-serif;
    font-weight: 300;
    box-shadow: 1px 2px 2px #00000040;
    justify-content: center;
    align-items: center;
    border: 2px solid transparent;
  }

  .item:hover {
    border: 2px solid var(--popup-text-color);
  }

  .icon {
    margin-inline: 0.25em;
    margin-top: 0.15em;
  }

  .overlay-dropdown {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    min-width: 180px;
    background: #fff;
    color: #000;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    z-index: 5;
    font-family: var(--font-body), sans-serif;
  }

  .overlay-dropdown-wide {
    min-width: 220px;
    max-width: 280px;
  }

  .overlay-dropdown-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.65rem 1rem;
    border: none;
    background: transparent;
    color: inherit;
    font-family: var(--font-body), sans-serif;
    font-size: 1rem;
    font-weight: 500;
    text-align: left;
    cursor: pointer;
  }

  .overlay-dropdown-option:hover {
    background: #f0f0f0;
  }

  :global(.popup .content-inner a) {
    color: var(--popup-text-color);
    font-weight: 700;
  }

  .popup-area {
    position: fixed;
    z-index: 2001;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    max-height: 100dvh;
    box-sizing: border-box;
    overflow: hidden;
  }

  .popup-center-column {
    position: relative;
    z-index: 99999;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    width: fit-content;
    max-width: calc(100vw - 4rem);
    max-height: calc(100vh - 3rem);
    margin: 1.5rem auto;
    box-sizing: border-box;
    pointer-events: none;
  }

  .popup-center-column > * {
    pointer-events: auto;
  }

  .popup-seq-nav {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    width: 100%;
    min-height: 2.25rem;
    box-sizing: border-box;
    align-self: center;
  }

  .popup-seq-nav-btn {
    appearance: none;
    border: none;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.15em;
    box-sizing: border-box;
    padding: 0.5rem 0.75rem;
    background: none;
    color: #fff;
    font-family: var(--font-heading), sans-serif;
    font-size: 1.5rem;
    font-weight: 400;
    line-height: 1.2;
    white-space: nowrap;
    text-shadow:
      3px 5px 6px rgb(0 0 0 / 30%),
      0 2px 8px rgba(0, 0, 0, 0.55);
    transition: opacity 0.15s ease, transform 0.15s ease;
  }

  .popup-seq-nav-btn :global(svg) {
    flex-shrink: 0;
    width: 1.35em;
    height: 1.35em;
    filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.85));
  }

  .popup-seq-nav-stop-badge {
    display: inline-flex;
    flex-shrink: 0;
    line-height: 0;
    filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.85));
  }

  .popup-seq-nav-stop-badge :global(svg) {
    width: 50px;
    height: 50px;
    filter: none;
  }

  .popup-seq-nav-btn:hover {
    opacity: 0.92;
    transform: translateY(-1px);
  }

  .popup-seq-nav-btn:active {
    opacity: 0.85;
    transform: translateY(0);
  }

  .popup-seq-nav-btn:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.9);
    outline-offset: 3px;
    border-radius: 4px;
  }

  .popup-modal.obscured {
    visibility: hidden;
    pointer-events: none;
    display: none;
  }

  .popup-bg {
    position: fixed;
    z-index: 2000;
    inset: 0;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    background: rgba(0, 0, 0, 0.66);
    backdrop-filter: blur(2px);
  }

  .popup-window-wrap {
    position: relative;
    display: flex;
    justify-content: center;
    width: fit-content;
    max-width: 100%;
    margin: 0;
    max-height: 100%;
    box-sizing: border-box;
    flex: 1 1 auto;
    min-height: 0;
  }

  .popup-modal.has-image .popup-center-column {
    width: 100%;
    max-width: calc(100vw - 4rem);
  }

  .popup-modal.has-image .popup-window-wrap {
    width: 100%;
  }

  .popup-layout {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    max-height: 95vh;
    max-width: 2000px;
    margin: auto;
  }

  .popup-content-column {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
  }

  .popup-modal.has-image .popup-content-column {
    flex: 1 1 auto;
    width: 100%;
  }

  .close-button-container {
    flex-shrink: 0;
  }

  .popup-window {
    position: relative;
    z-index: 99999;
    background: transparent;
    width: auto;
    max-width: 95vw;
    max-height: 95vh;
    margin: 0;
    overflow: visible;
  }

  .popup-content {
    flex: 0 1 auto;
    border-radius: 10px;
    background: var(--popup-bg-color);
    color: var(--popup-text-color);
    overflow: hidden;
    width: 40rem;
    max-width: calc(100vw - 4rem - 50px - 1rem);
    max-height: 95vh;
    padding: 0;
  }

  .popup-modal.has-image .popup-window {
    width: 90%;
  }

  .popup-modal.has-image .popup-content {
    flex: 1 1 auto;
    position: relative;
    width: 100%;
    padding-bottom: 45%;
  }

  .popup-modal.has-image .popup-content .popup {
    position: absolute;
    inset: 0;
  }

  .popup-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Desktop: no visible track; overlay-style scrollbar */
  .popup-content::-webkit-scrollbar,
  .content-inner::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 8px;
    height: 8px;
  }

  .popup-content::-webkit-scrollbar-track,
  .content-inner::-webkit-scrollbar-track {
    background: transparent;
  }

  .popup-content::-webkit-scrollbar-thumb,
  .content-inner::-webkit-scrollbar-thumb {
    background: color-mix(in srgb, var(--popup-text-color, #fff) 45%, transparent);
    border-radius: 999px;
  }

  .popup-content::-webkit-scrollbar-thumb:hover,
  .content-inner::-webkit-scrollbar-thumb:hover {
    background: var(--popup-text-color, #fff);
  }

  .popup-content,
  .content-inner {
    scrollbar-gutter: auto;
    scrollbar-width: thin;
    scrollbar-color: color-mix(in srgb, var(--popup-text-color, #fff) 45%, transparent) transparent;
  }

  @media only screen and (max-width: 600px) {
    .popup-content::-webkit-scrollbar-track,
    .content-inner::-webkit-scrollbar-track {
      background: color-mix(in srgb, var(--popup-text-color, #fff) 18%, transparent);
      border-radius: 999px;
    }

    .popup-content::-webkit-scrollbar-thumb,
    .content-inner::-webkit-scrollbar-thumb {
      background: color-mix(in srgb, var(--popup-text-color, #fff) 75%, transparent);
    }

    .popup-content,
    .content-inner {
      scrollbar-gutter: stable;
      scrollbar-color: color-mix(in srgb, var(--popup-text-color, #fff) 75%, transparent)
        color-mix(in srgb, var(--popup-text-color, #fff) 18%, transparent);
    }

    .popup-area {
      /* Leave room above the mobile map menu bar (Locations / Search). */
      padding:
        max(0.5rem, env(safe-area-inset-top, 0px))
        max(1rem, env(safe-area-inset-right, 0px))
        calc(72px + env(safe-area-inset-bottom, 0px) + 0.75rem)
        max(1rem, env(safe-area-inset-left, 0px));
      justify-content: center;
      align-items: center;
      overflow: hidden;
      box-sizing: border-box;
    }


    .popup-center-column {
      width: 100% !important;
      max-width: 100%;
      max-height: 100%;
      min-height: 0;
      margin: 0;
      gap: 0;
      display: flex;
      flex-direction: column;
      flex: 0 1 auto;
    }

    .popup-window-wrap {
      width: 100% !important;
      max-width: 100%;
      max-height: 100%;
      min-height: 0;
      flex: 1 1 auto;
      height: auto;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .popup-content-column {
      display: flex;
      flex-direction: column;
      width: 100%;
      min-width: 0;
      flex: 1 1 auto;
      min-height: 0;
      overflow: hidden;
    }

    .popup-seq-nav {
      display: flex;
      justify-content: center;
      flex-shrink: 0;
      min-height: 2rem;
      padding: 0.1rem 0.15rem 0;
    }

    .popup-modal.has-seq-nav .popup-layout {
      position: relative;
    }

    .popup-modal.has-seq-nav .close-button-container {
      position: absolute;
      top: 0.35rem;
      right: 0.35rem;
      z-index: 10;
      align-self: auto;
    }

    .popup-modal.has-seq-nav .close-button-container :global(.close-container) {
      width: 40px;
      height: 40px;
      margin-bottom: 0;
      filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.55));
    }

    .popup-modal.has-seq-nav .close-button-container :global(.leftright),
    .popup-modal.has-seq-nav .close-button-container :global(.rightleft) {
      width: 40px;
      margin-top: 19px;
    }

    .popup-modal.has-seq-nav:not(.has-image) .content-inner {
      padding-right: 2.75rem;
    }

    .popup-seq-nav-btn {
      width: auto;
      font-size: 1.15rem;
      padding: 0.45rem 0.5rem;
    }

    .popup-window {
      width: 100% !important;
      max-width: 100% !important;
      max-height: 100%;
      min-height: 0;
      flex: 1 1 auto;
      height: auto;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .popup-content {
      width: 100%;
      max-width: 100%;
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }

    .popup-modal.has-image .popup-content {
      padding-bottom: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .popup-modal.has-image .popup-content .popup {
      position: static;
      flex: 1;
      min-height: 0;
      min-width: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .popup-modal.has-image .content-inner {
      flex: 1;
      min-height: 0;
      min-width: 0;
      overflow-y: scroll;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
      max-height: none;
    }

    .has-image .popup-window-wrap,
    .has-image .popup-window {
      max-height: 100%;
      height: auto !important;
    }

    .has-image .image.column {
      height: auto;
      width: 100%;
      flex: none;
      flex-shrink: 0;
      min-height: 0;
      aspect-ratio: 4/3;
    }

    .has-image .content.column {
      flex: 1;
      min-height: 0;
      min-width: 0;
      width: 100%;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .content {
      justify-content: flex-start;
    }

    .title {
      font-size: 1.75em;
    }

    .content-inner {
      font-size: 14px;
      height: auto;
      max-height: none;
      overflow-y: visible;
      padding: 1.25rem;
      padding-top: 0;
      flex: 1 1 auto;
      min-height: 0;
    }

    .popup-layout {
      flex-direction: column;
      align-items: stretch;
      gap: 0.5rem;
      max-height: 100%;
      min-height: 0;
      flex: 1 1 auto;
      width: 100%;
      overflow: hidden;
    }

    .close-button-container {
      flex-shrink: 0;
      align-self: center;
    }

    .popup {
      height: auto;
      width: 100%;
      flex-direction: column;
      flex-wrap: nowrap;
    }

    .items {
      padding: 0.25rem;
    }

    .item {
      padding: 0.5rem 1.25rem;
      font-size: 1.1rem;
    }

    .gallery-arrow {
      width: 32px;
      height: 32px;
    }

    .gallery-arrow-prev {
      left: 8px;
    }

    .gallery-arrow-next {
      right: 10px;
    }
  }
</style>
